import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { AssignmentListRow } from './AssignmentContainer';

import {
  useCreateWorkAssignmentListAttachment,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import {
  useCreateWorkAssignment,
  useUpdateWorkAssignment,
  useWorkAssignments,
  type ResourceNeeds,
  type WorkAssignment,
} from 'components/IAP/hooks/workAssignmentHooks';
import {
  useCreateIncidentComs,
  useIncidentComs,
  useUpdateIncidentComs,
  type IncidentComs,
} from 'components/IAP/hooks/incidentComsHooks';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import {
  useGetPositionsForIncident,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import { selectGroupGuid } from 'slices/commonSelectors';
import { SectionChiefInfo } from './useSectionChiefResolver';
import { PersonnelSectionTableEdit } from './PersonnelSectionTable';
import {
  buildRosterNameOptions,
  findRosterContactForName,
  type PersonnelContactField,
  type PersonnelNameField,
} from './personnelSection';
import {
  ICS204_ASSIGN_NEEDS_IAP,
  statusOptions,
  statusSideEffectWarning,
  toProposedStatusEnum,
  type Status,
} from './assignmentStatus';
import { useIapCoverApproved } from './useIapCoverApproved';
import AssigneeInfoLabel from './AssigneeInfoLabel';
import AssignmentCardResourceSectionEdit from './AssignmentCardResourceSectionEdit';
import AssignmentCardWorkAssignmentsEdit from './AssignmentCardWorkAssignmentsEdit';
import AssignmentCardCommunicationsEdit from './AssignmentCardCommunicationsEdit';
import usePlannedResourceResolver from './usePlannedResourceResolver';
import {
  ICS204_CONTENT,
  ICS204_HEADER_ROW,
  ICS204_ICON_BTN,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ics204SummaryGridTemplate,
} from './assignmentCardStyles';
import {
  Ics204SelectField,
  Ics204RichTextField,
  Ics204TextField,
  type Ics204SelectOption,
} from './Ics204Fields';
import Ics204LocationSelect from './Ics204LocationSelect';
import {
  plannedResourceFromDraft,
  type AssignmentCardFormData,
  type CommunicationDraft,
  type ResourceNeedDraft,
  type WorkAssignmentDraft,
} from './assignmentCardFormTypes';
import { ICS204_BOX_LABELS, Ics204CardSection } from './ics204BoxLabels';
import { Ics204IncidentPeriodHeader } from './Ics204IncidentPeriodHeader';
import Ics204VersioningPanel from './Ics204VersioningPanel';
import { parseWalaSignatures } from './ics204SignatureSlots';
import { ics204HtmlToPlainText } from './ics204RichText';
import type { WalaVersionRow } from './Ics204VersionsDialog';

export type { AssignmentCardFormData } from './assignmentCardFormTypes';

type AssignmentCardEditProps = {
  row: AssignmentListRow;
  setIsEditing: (isEditing: boolean) => void;
  columnNumber: number;
  sectionChief?: SectionChiefInfo;
  periodNumber: number;
  alreadyAddedWorkGroupIds?: string[];
  isCreatingSignedVersion?: boolean;
  viewingVersionId?: string | null;
  onStartCreateSignedVersion?: () => void;
  onCancelCreateSignedVersion?: () => void;
  onSignedVersionCreated?: () => void;
  onUnsignedVersionCreated?: () => void;
  onViewVersion?: (version: WalaVersionRow) => void;
  onViewLatest?: () => void;
};

/** Drops rows the user added but left entirely blank. */
const isBlankResourceNeed = (need: ResourceNeedDraft) =>
  !String(need.resourceName ?? '').trim() &&
  !String(need.required ?? '').trim() &&
  !String(need.have ?? '').trim() &&
  !String(need.need ?? '').trim();

const toResourceNeeds = (needs: ResourceNeedDraft[]): ResourceNeeds[] =>
  needs
    .filter((need) => !isBlankResourceNeed(need))
    .map((need) => ({
      resourceName: need.resourceName,
      required: Number(need.required) || 0,
      have: Number(need.have) || 0,
      need: Number(need.need) || 0,
    }));

const sameInstant = (a: string, b: Date | string | undefined | null) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
};

/**
 * Both entities version on every PUT, so untouched rows are skipped rather than
 * accumulating identical versions each time the card is saved.
 */
const workAssignmentChanged = (
  draft: WorkAssignmentDraft,
  server: WorkAssignment
) =>
  draft.name !== (server.work_assignment ?? '') ||
  draft.overheadPosition !== (server.overhead_positions ?? '') ||
  draft.specialInstructions !== (server.special_instructions ?? '') ||
  draft.reportingLocation !== (server.reporting_location ?? '') ||
  !sameInstant(draft.requestedArrivalTime, server.requested_arrival_time) ||
  JSON.stringify(toResourceNeeds(draft.resourceNeeds)) !==
    JSON.stringify(
      toResourceNeeds((server.resource_list ?? []) as ResourceNeedDraft[])
    );

const communicationChanged = (
  fields: Omit<CommunicationDraft, 'id' | 'archived'>,
  server: IncidentComs
) =>
  (Object.keys(fields) as (keyof typeof fields)[]).some(
    (key) => (fields[key] ?? '') !== (server[key] ?? '')
  );

type AssignmentCardEditFormProps = AssignmentCardEditProps & {
  serverWorkAssignments: WorkAssignment[];
  serverCommunications: IncidentComs[];
};

const AssignmentCardEditForm = ({
  row,
  setIsEditing,
  columnNumber,
  sectionChief,
  periodNumber,
  alreadyAddedWorkGroupIds = [],
  isCreatingSignedVersion = false,
  viewingVersionId = null,
  onStartCreateSignedVersion,
  onCancelCreateSignedVersion,
  onSignedVersionCreated,
  onUnsignedVersionCreated,
  onViewVersion,
  onViewLatest,
  serverWorkAssignments,
  serverCommunications,
}: AssignmentCardEditFormProps) => {
  // Todo (aeortic): This memo is shared between this and the view mode component.
  // We should consider creating a hook for this.
  const plannedResourceData = usePlannedResourceResolver(
    row.plannedResources,
    row.walaId
  );

  const groupGuid = useSelector(selectGroupGuid);
  const { data: positions } = useGetPositionsForIncident();
  const { data: roster } = useGetRosterForIncident();
  const { data: draftRoster } = useGetDraftRosterMembersForIncident();

  const workGroupOptions = useMemo<Ics204SelectOption[]>(
    () =>
      (positions ?? [])
        .filter(
          (position) => position.is_workgroup && !position.isResourcePosition
        )
        .map((position) => {
          const claimedByAnotherCard =
            position.id !== row.workGroupId &&
            alreadyAddedWorkGroupIds.includes(position.id);
          return {
            value: position.id,
            label: claimedByAnotherCard
              ? `${position.name} (already added)`
              : position.name,
            disabled: claimedByAnotherCard,
          };
        }),
    [positions, row.workGroupId, alreadyAddedWorkGroupIds]
  );

  const rosterNameOptions = useMemo(
    () =>
      buildRosterNameOptions(roster, draftRoster, [
        row.sectionChief,
        row.branchDirector,
        row.divisionGroupSupervisor,
      ]),
    [
      roster,
      draftRoster,
      row.sectionChief,
      row.branchDirector,
      row.divisionGroupSupervisor,
    ]
  );

  const currentProposedStatus = toProposedStatusEnum(row.proposedStatus);
  const iapApproved = useIapCoverApproved(periodNumber);
  const statusSelectOptions = useMemo(
    () =>
      statusOptions(
        row.actualStatus as Status | null,
        currentProposedStatus,
        iapApproved
      ),
    [row.actualStatus, currentProposedStatus, iapApproved]
  );

  const formMethods = useForm<AssignmentCardFormData>({
    mode: 'onChange',
    defaultValues: {
      walaId: row.walaId,
      sectionChief: row.sectionChief ?? '',
      sectionChiefContact: row.sectionChiefContact ?? '',
      workGroupId: row.workGroupId,
      branch: row.branch,
      division: row.division,
      walaGroup: row.walaGroup,
      stagingArea: row.stagingArea,
      proposedStatus: currentProposedStatus,
      branchDirector: row.branchDirector,
      branchDirectorContact: row.branchDirectorContact ?? '',
      divisionGroupSupervisor: row.divisionGroupSupervisor,
      divisionGroupSupervisorContact: row.divisionGroupSupervisorContact ?? '',
      resource: plannedResourceData.reduce(
        (acc, res) => {
          acc[res.id] = {
            resourceName: res.name,
            leader: res.leader,
            numberOfPersons: res.numberOfPersons,
            contactInfo: res.contactInfo,
            reportingInfo: res.reportingInfo,
            has204a: res.has204a,
            isDocumentationEntry: !!res.isDocumentationEntry,
            resourceBranch: res.resourceBranch,
            resourceDivision: res.resourceDivision,
            resourceGroup: res.resourceGroup,
            resourceStrikeTeam: res.resourceStrikeTeam,
            resourceTaskForce: res.resourceTaskForce,
            resourceAssignmentLocation: res.resourceAssignmentLocation,
            resourceSpecialInstructions: res.resourceSpecialInstructions,
            resourceSafetyPlanLocation: res.resourceSafetyPlanLocation,
          };
          return acc;
        },
        {} as NonNullable<AssignmentCardFormData['resource']>
      ),
      workAssignments: serverWorkAssignments.map<WorkAssignmentDraft>((wa) => ({
        id: wa.id,
        name: wa.work_assignment ?? '',
        overheadPosition: wa.overhead_positions ?? '',
        specialInstructions: wa.special_instructions ?? '',
        reportingLocation: wa.reporting_location ?? '',
        requestedArrivalTime: wa.requested_arrival_time
          ? new Date(wa.requested_arrival_time).toISOString()
          : '',
        resourceNeeds: (wa.resource_list ?? []).map((need) => ({
          resourceName: need.resourceName ?? '',
          required: need.required ?? 0,
          have: need.have ?? 0,
          need: need.need ?? 0,
        })),
        archived: false,
      })),
      communications: serverCommunications.map<CommunicationDraft>((com) => ({
        id: com.id,
        archived: false,
        type: com.type ?? 'Radio',
        channel_number: com.channel_number ?? '',
        function: com.function ?? '',
        channel_name: com.channel_name ?? '',
        work_group_id: com.work_group_id ?? '',
        rx_frequency: com.rx_frequency ?? '',
        rx_tone_nac: com.rx_tone_nac ?? '',
        tx_frequency: com.tx_frequency ?? '',
        tx_tone_nac: com.tx_tone_nac ?? '',
        mode: com.mode ?? '',
        remarks: com.remarks ?? '',
        cell_phone_number: com.cell_phone_number ?? '',
        other: com.other ?? '',
      })),
      specialInstructions: row.specialInstructions,
      medicalCommunication: row.medicalCommunication,
      evacuationCommunication: row.evacuationCommunication,
      otherCommunication: row.otherCommunication,
    },
  });

  const { handleSubmit, setValue, getValues } = formMethods;

  const handlePersonnelNameSelect = (
    _nameField: PersonnelNameField,
    contactField: PersonnelContactField,
    nextName: string
  ) => {
    const contact = findRosterContactForName(nextName, roster, draftRoster);
    setValue(contactField, contact, { shouldDirty: true });
  };

  const { mutateAsync: createWala } = useCreateWorkAssignmentListAttachment();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const { mutateAsync: createWorkAssignment } = useCreateWorkAssignment();
  const { mutateAsync: updateWorkAssignment } = useUpdateWorkAssignment();
  const { mutateAsync: createComs } = useCreateIncidentComs();
  const { mutateAsync: updateComs } = useUpdateIncidentComs();

  const [isSaving, setIsSaving] = useState(false);
  const [pendingData, setPendingData] = useState<AssignmentCardFormData | null>(
    null
  );

  const persist = async (data: AssignmentCardFormData) => {
    const editedResources = Object.entries(data.resource ?? {}).map(
      ([id, res]) => plannedResourceFromDraft(id, res)
    );

    const walaPayload = {
      workGroupId: data.workGroupId,
      branch: data.branch,
      division: data.division,
      walaGroup: data.walaGroup,
      stagingArea: data.stagingArea,
      // Only sent when the user actually picked a new status: the backend turns
      // a resent `completed`/`incomplete` into a fresh review request.
      ...(data.proposedStatus !== currentProposedStatus
        ? { proposedStatus: data.proposedStatus }
        : {}),
      sectionChief: data.sectionChief,
      sectionChiefContact: data.sectionChiefContact,
      branchDirector: data.branchDirector,
      branchDirectorContact: data.branchDirectorContact,
      divisionGroupSupervisor: data.divisionGroupSupervisor,
      divisionGroupSupervisorContact: data.divisionGroupSupervisorContact,
      plannedResources: editedResources,
      medicalCommunication: data.medicalCommunication,
      evacuationCommunication: data.evacuationCommunication,
      otherCommunication: data.otherCommunication,
      specialInstructions: data.specialInstructions,
    };

    const walaOp = data.walaId
      ? updateWala({ walaId: data.walaId, ...walaPayload })
      : createWala({ periodNumber, ...walaPayload });

    const serverById = new Map<string, WorkAssignment>(
      serverWorkAssignments.map((wa) => [wa.id, wa])
    );

    const ops: Promise<unknown>[] = [walaOp];

    for (const draft of data.workAssignments ?? []) {
      const server = draft.id ? serverById.get(draft.id) : undefined;

      if (!draft.id) {
        if (!draft.name.trim()) continue;
        ops.push(
          createWorkAssignment({
            work_assignment: draft.name,
            status: 'Planned',
            parent_tactics: [],
            work_group_id: data.workGroupId,
            // The card's `periodNumber` is already the period being planned.
            operational_period_number: periodNumber,
            overhead_positions: draft.overheadPosition,
            special_instructions: draft.specialInstructions,
            reporting_location: draft.reportingLocation,
            requested_arrival_time: draft.requestedArrivalTime
              ? new Date(draft.requestedArrivalTime)
              : undefined,
            // Create reads `resources`; update reads `resource_list`.
            resources: toResourceNeeds(draft.resourceNeeds),
          })
        );
        continue;
      }

      if (!server) continue;

      if (draft.archived) {
        ops.push(
          updateWorkAssignment({
            ...server,
            id: server.id,
            archived: true,
          })
        );
        continue;
      }

      // Every PUT inserts a new version row, so untouched rows are left alone.
      if (!workAssignmentChanged(draft, server)) continue;

      ops.push(
        updateWorkAssignment({
          ...server,
          id: server.id,
          work_assignment: draft.name,
          overhead_positions: draft.overheadPosition,
          special_instructions: draft.specialInstructions,
          reporting_location: draft.reportingLocation,
          requested_arrival_time: draft.requestedArrivalTime
            ? new Date(draft.requestedArrivalTime)
            : undefined,
          resource_list: toResourceNeeds(draft.resourceNeeds),
        })
      );
    }

    const comsById = new Map(serverCommunications.map((com) => [com.id, com]));

    for (const draft of data.communications ?? []) {
      const { id, archived, ...fields } = draft;
      const server = id ? comsById.get(id) : undefined;

      if (!id) {
        if (!fields.channel_name?.trim() && !fields.function?.trim()) continue;
        ops.push(
          createComs({
            ...fields,
            type: fields.type ?? 'Radio',
            archived: false,
            // The hook creates the org-level coms record first, which requires
            // the group.
            group_guid: groupGuid,
            // Rows added here belong to this card's work group.
            work_group_id: data.workGroupId,
          })
        );
        continue;
      }

      if (!server) continue;
      if (!archived && !communicationChanged(fields, server)) continue;

      const { assignment: _assignment, ...serverWithoutAssignment } = server;
      ops.push(
        updateComs({
          comsId: server.id,
          data: {
            ...serverWithoutAssignment,
            ...fields,
            type: fields.type ?? server.type ?? 'Radio',
            assignment: undefined,
            archived,
          },
        })
      );
    }

    await Promise.all(ops);
  };

  const save = async (data: AssignmentCardFormData) => {
    setIsSaving(true);
    try {
      await persist(data);
      setIsEditing(false);
    } catch {
      // The mutation hooks already surface the failure as a toast; keeping the
      // form open preserves what the user typed so they can retry.
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmit = async (data: AssignmentCardFormData) => {
    const statusChanged = data.proposedStatus !== currentProposedStatus;
    if (statusChanged && statusSideEffectWarning(data.proposedStatus)) {
      setPendingData(data);
      return;
    }
    await save(data);
  };

  const gridTemplateColumns = ics204SummaryGridTemplate(columnNumber);

  const getSignedVersionPayload = () => {
    const data = getValues();
    const editedResources = Object.entries(data.resource ?? {}).map(
      ([id, res]) => plannedResourceFromDraft(id, res)
    );
    return {
      workGroupId: data.workGroupId,
      branch: data.branch,
      division: data.division,
      walaGroup: data.walaGroup,
      stagingArea: data.stagingArea,
      sectionChief: data.sectionChief,
      sectionChiefContact: data.sectionChiefContact,
      branchDirector: data.branchDirector,
      branchDirectorContact: data.branchDirectorContact,
      divisionGroupSupervisor: data.divisionGroupSupervisor,
      divisionGroupSupervisorContact: data.divisionGroupSupervisorContact,
      plannedResources: editedResources,
      medicalCommunication: data.medicalCommunication,
      evacuationCommunication: data.evacuationCommunication,
      otherCommunication: data.otherCommunication,
      specialInstructions: data.specialInstructions,
    };
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="ics204-edit-form">
        <div
          className={ICS204_HEADER_ROW}
          style={{ gridTemplateColumns }}
          data-testid="ics204-edit-header"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Collapse assignment"
            className={ICS204_ICON_BTN}
            data-testid="ics204-edit-collapse-button"
            onClick={() => {
              if (isCreatingSignedVersion) {
                onCancelCreateSignedVersion?.();
              } else {
                setIsEditing(false);
              }
            }}
          >
            <ChevronDown className="tw:h-4 tw:w-4" />
          </Button>
          <Ics204SelectField
            label={<AssigneeInfoLabel />}
            fieldName="workGroupId"
            testId="ics204-edit-work-group"
            className="tw:pr-2"
            options={workGroupOptions}
            rules={{ required: 'A work group is required' }}
          />
          <div className="tw:pr-2">
            <Ics204SelectField
              label="Status"
              fieldName="proposedStatus"
              testId="ics204-edit-status"
              options={statusSelectOptions}
              disabled={isCreatingSignedVersion}
            />
            {!iapApproved && currentProposedStatus !== 'in_progress' ? (
              <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
                {ICS204_ASSIGN_NEEDS_IAP}
              </p>
            ) : null}
          </div>
        </div>
        <Ics204VersioningPanel
          walaId={row.walaId}
          tipId={row.id}
          tipSignatures={parseWalaSignatures(row.signatures)}
          tipUpdatedAt={row.updatedAt}
          tipUpdatedByUserName={row.updatedByUserName}
          isCreatingSignedVersion={isCreatingSignedVersion}
          viewingVersionId={viewingVersionId}
          getSignedVersionPayload={getSignedVersionPayload}
          onStartCreateSignedVersion={
            onStartCreateSignedVersion ?? (() => undefined)
          }
          onCancelCreateSignedVersion={
            onCancelCreateSignedVersion ?? (() => setIsEditing(false))
          }
          onSignedVersionCreated={
            onSignedVersionCreated ?? (() => setIsEditing(false))
          }
          onUnsignedVersionCreated={
            onUnsignedVersionCreated ?? (() => undefined)
          }
          onViewVersion={onViewVersion ?? (() => undefined)}
          onViewLatest={onViewLatest ?? (() => undefined)}
        >
          <div className={ICS204_CONTENT}>
            <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
              <Ics204IncidentPeriodHeader periodNumber={periodNumber} />
              <Ics204CardSection
                boxLabel={ICS204_BOX_LABELS.assignedLocation}
                testId="ics204-section-assigned-location"
                className="tw:mt-0 tw:h-full"
              >
                <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
                  <Ics204LocationSelect
                    label="Branch"
                    fieldName="branch"
                    testId="ics204-edit-branch"
                    category="Branch"
                    positions={positions ?? []}
                    className="tw:min-w-0"
                  />
                  <Ics204LocationSelect
                    label="Division"
                    fieldName="division"
                    testId="ics204-edit-division"
                    category="Division"
                    positions={positions ?? []}
                    className="tw:min-w-0"
                  />
                  <Ics204LocationSelect
                    label="Group"
                    fieldName="walaGroup"
                    testId="ics204-edit-group"
                    category="Group"
                    positions={positions ?? []}
                    className="tw:min-w-0"
                  />
                  <Ics204LocationSelect
                    label="Staging Area"
                    fieldName="stagingArea"
                    testId="ics204-edit-staging-area"
                    category="Staging Area"
                    positions={positions ?? []}
                    className="tw:min-w-0"
                  />
                </div>
              </Ics204CardSection>
            </div>
            <Ics204CardSection
              boxLabel={ICS204_BOX_LABELS.personnel}
              testId="ics204-section-personnel"
            >
              <PersonnelSectionTableEdit
                options={rosterNameOptions}
                onNameSelect={handlePersonnelNameSelect}
              />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel={ICS204_BOX_LABELS.resourcesAssigned}
              testId="ics204-section-resources-assigned"
            >
              <AssignmentCardResourceSectionEdit
                walaId={row.walaId}
                periodNumber={periodNumber}
              />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel={ICS204_BOX_LABELS.workAssignments}
              testId="ics204-section-work-assignments"
            >
              <AssignmentCardWorkAssignmentsEdit />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel={ICS204_BOX_LABELS.specialInstructions}
              testId="ics204-section-special-instructions"
            >
              <Ics204RichTextField
                fieldName="specialInstructions"
                testId="ics204-edit-special-instructions"
                rules={{
                  validate: (value: string) => {
                    const plain = ics204HtmlToPlainText(value);
                    if (plain.length > 1000) {
                      return 'This maximum length is 1000 characters';
                    }
                    return true;
                  },
                }}
              />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel={ICS204_BOX_LABELS.communications}
              testId="ics204-section-communications"
              className="tw:mt-3 tw:mb-3"
            >
              <AssignmentCardCommunicationsEdit />
              <div className="tw:mt-3 tw:font-semibold">
                Emergency Communication
              </div>
              <div className="tw:mt-2 tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
                <Ics204TextField
                  label="Medical Communication"
                  fieldName="medicalCommunication"
                  testId="ics204-edit-medical-communication"
                />
                <Ics204TextField
                  label="Evacuation Communication"
                  fieldName="evacuationCommunication"
                  testId="ics204-edit-evacuation-communication"
                />
                <Ics204TextField
                  label="Other Communication"
                  fieldName="otherCommunication"
                  testId="ics204-edit-other-communication"
                />
              </div>
            </Ics204CardSection>
          </div>
        </Ics204VersioningPanel>
        <div className="tw:m-3 tw:flex tw:items-center tw:gap-2">
          {!isCreatingSignedVersion ? (
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save as Draft'}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className={ICS204_OUTLINE_BTN}
            disabled={isSaving}
            onClick={() => {
              if (isCreatingSignedVersion) {
                onCancelCreateSignedVersion?.();
              } else {
                setIsEditing(false);
              }
            }}
          >
            Cancel
          </Button>
        </div>
      </form>

      <AlertDialog
        open={pendingData !== null}
        onOpenChange={(open) => {
          if (!open) setPendingData(null);
        }}
      >
        {/* Portals to the body, escaping the card's `dark` ancestor, so it has
            to declare the token set itself. */}
        <AlertDialogContent
          className="dark"
          data-testid="ics204-status-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Change assignment status?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingData
                ? statusSideEffectWarning(pendingData.proposedStatus)
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ics204-status-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ics204-status-confirm-accept"
              onClick={async () => {
                const data = pendingData;
                setPendingData(null);
                if (data) await save(data);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
};

/**
 * Waits for the work assignment and communications caches before mounting the
 * form. Both seed `defaultValues`, which react-hook-form reads once, so a cold
 * cache would leave those sections empty until the card is reopened.
 */
const AssignmentCardEdit = (props: AssignmentCardEditProps) => {
  const { row, periodNumber } = props;
  const { data: allWorkAssignments, isPending: workAssignmentsPending } =
    useWorkAssignments();
  const { data: allIncidentComs, isPending: comsPending } =
    useIncidentComs(INCIDENT);

  /**
   * Work assignments and communications are separate entities joined to this
   * card by work group, so their rows come from their own caches rather than
   * being threaded through the assignment list item.
   */
  const serverWorkAssignments = useMemo(
    () =>
      (allWorkAssignments ?? []).filter(
        (wa) =>
          wa.work_group_id === row.workGroupId &&
          wa.operational_period_number === periodNumber &&
          !wa.archived
      ),
    [allWorkAssignments, row.workGroupId, periodNumber]
  );

  const serverCommunications = useMemo(
    () =>
      (allIncidentComs ?? []).filter(
        (com) => com.work_group_id === row.workGroupId && !com.archived
      ),
    [allIncidentComs, row.workGroupId]
  );

  if (workAssignmentsPending || comsPending) {
    return (
      <div className={`${ICS204_CONTENT} ${ICS204_MUTED}`}>
        Loading assignment details…
      </div>
    );
  }

  return (
    <AssignmentCardEditForm
      {...props}
      serverWorkAssignments={serverWorkAssignments}
      serverCommunications={serverCommunications}
    />
  );
};

export default AssignmentCardEdit;
